import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, GripVertical, Plus, Trash2 } from 'lucide-react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useCoarsePointer } from '@/hooks/use-coarse-pointer';
import { cn } from '@/lib/utils';
import {
  MARGIN_PROCEDURES,
  V1_SELECTABLE_VAT_RATES,
  type DraftFieldErrorCode,
  type MarginProcedure,
  type TaxScheme,
  type VatRate,
} from './api';
import { sanitizeDecimal, type LineDraft } from './content';
import { fieldErrorKey } from './fieldErrors';

/** Suggested units offered by the unit combobox; the field stays free-text. */
const UNIT_OPTIONS = ['szt.', 'usł.'] as const;

/**
 * Per-row line editor: name (multi-line, growing with its text), quantity, unit, and a price. Under
 * the regular scheme the price is net and the row ends with a VAT-rate picker; a margin invoice
 * discloses no rate at all, so the row is priced gross and the rate cell is gone entirely rather than
 * shown empty.
 */
export function LinesEditor({
  lines,
  lineErrors,
  scheme = 'REGULAR',
  disabled,
  onPatch,
  onAdd,
  onRemove,
  onMove,
}: {
  lines: LineDraft[];
  /** Field-error codes keyed by line key (stable across reorder), not by position. */
  lineErrors: Map<number, DraftFieldErrorCode[]>;
  scheme?: TaxScheme;
  disabled?: boolean;
  onPatch: (key: number, patch: Partial<LineDraft>) => void;
  onAdd: () => void;
  onRemove: (key: number) => void;
  /** Reorder: move the `from` line to the position of the `to` line. */
  onMove: (fromKey: number, toKey: number) => void;
}) {
  const { t } = useTranslation();
  const sensors = useSensors(
    // A small drag threshold so a click on the handle isn't read as a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const reorderable = !disabled && lines.length > 1;

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) onMove(active.id as number, over.id as number);
  };

  return (
    <div className="space-y-2">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={lines.map((l) => l.key)}
          strategy={verticalListSortingStrategy}
          disabled={!reorderable}
        >
          <div className="space-y-2">
            {lines.map((line, index) => (
              <LineRow
                key={line.key}
                line={line}
                position={index + 1}
                codes={lineErrors.get(line.key) ?? []}
                scheme={scheme}
                disabled={disabled}
                reorderable={reorderable}
                singleLine={lines.length === 1}
                onPatch={onPatch}
                onRemove={onRemove}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        onClick={onAdd}
      >
        <Plus data-icon="inline-start" />
        {t('invoices.compose.lines.add')}
      </Button>
    </div>
  );
}

/** One sortable line row. Owns its `useSortable` binding; the grip handle carries the drag listeners. */
function LineRow({
  line,
  position,
  codes,
  scheme,
  disabled,
  reorderable,
  singleLine,
  onPatch,
  onRemove,
}: {
  line: LineDraft;
  position: number;
  codes: DraftFieldErrorCode[];
  scheme: TaxScheme;
  disabled?: boolean;
  reorderable: boolean;
  singleLine: boolean;
  onPatch: (key: number, patch: Partial<LineDraft>) => void;
  onRemove: (key: number) => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: line.key,
    disabled: !reorderable,
  });
  const errorFor = (code: DraftFieldErrorCode) =>
    codes.includes(code) ? t(fieldErrorKey(code)) : undefined;
  const margin = scheme === 'MARGIN';
  const nameError = errorFor('LINE_NAME_MISSING') ?? errorFor('LINE_NAME_TOO_LONG');
  const quantityError = errorFor('LINE_QUANTITY_INVALID');
  const priceError = errorFor(margin ? 'LINE_UNIT_GROSS_PRICE_INVALID' : 'LINE_UNIT_PRICE_INVALID');
  const rateError = errorFor('LINE_VAT_RATE_INVALID');

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn('rounded-md border p-3', isDragging && 'relative z-10 opacity-80 shadow-lg')}
    >
      {/* One row on wide screens; folds (wraps) once the fields no longer fit. */}
      <div className="flex flex-wrap items-start gap-2">
        {reorderable ? (
          <button
            type="button"
            aria-label={t('invoices.compose.lines.reorder')}
            className="flex h-8 shrink-0 cursor-grab touch-none items-center text-muted-foreground outline-none hover:text-foreground focus-visible:text-foreground active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="size-4" />
          </button>
        ) : null}
        <span className="flex h-8 shrink-0 items-center text-sm text-muted-foreground tabular-nums">
          {position}
        </span>
        <LineField className="min-w-48 flex-1" error={nameError}>
          <Textarea
            aria-label={t('invoices.compose.fields.item_name')}
            placeholder={t('invoices.compose.fields.item_name')}
            autoComplete="off"
            rows={1}
            className="field-sizing-content max-h-48 min-h-8 resize-none py-1"
            disabled={disabled}
            aria-invalid={nameError ? true : undefined}
            value={line.name}
            onChange={(e) => onPatch(line.key, { name: e.target.value })}
          />
        </LineField>
        <LineField className="w-24 shrink-0" error={quantityError}>
          <Input
            aria-label={t('invoices.compose.fields.quantity')}
            placeholder={t('invoices.compose.fields.quantity')}
            inputMode="decimal"
            autoComplete="off"
            disabled={disabled}
            aria-invalid={quantityError ? true : undefined}
            value={line.quantity}
            onChange={(e) => onPatch(line.key, { quantity: sanitizeDecimal(e.target.value) })}
          />
        </LineField>
        <LineField className="w-24 shrink-0">
          <UnitPicker
            value={line.unitOfMeasure}
            disabled={disabled}
            onChange={(unit) => onPatch(line.key, { unitOfMeasure: unit })}
          />
        </LineField>
        <LineField className="w-36 shrink-0" error={priceError}>
          <PriceInput
            label={
              margin
                ? t('invoices.compose.fields.unit_gross_price')
                : t('invoices.compose.fields.unit_net_price')
            }
            value={margin ? line.unitGrossPrice : line.unitNetPrice}
            disabled={disabled}
            invalid={Boolean(priceError)}
            onChange={(price) =>
              onPatch(line.key, margin ? { unitGrossPrice: price } : { unitNetPrice: price })
            }
          />
        </LineField>
        {margin ? null : (
          <LineField className="w-28 shrink-0" error={rateError}>
            <RatePicker
              value={line.vatRate}
              disabled={disabled}
              invalid={Boolean(rateError)}
              onChange={(rate) => onPatch(line.key, { vatRate: rate })}
            />
          </LineField>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0"
          disabled={disabled || singleLine}
          aria-label={t('invoices.compose.lines.remove')}
          onClick={() => onRemove(line.key)}
        >
          <Trash2 />
        </Button>
      </div>
    </div>
  );
}

/**
 * A field slot: fixes the field's width in the folding row and, when the evaluator flagged it, turns
 * the field red (via the child's `aria-invalid`). On a mouse the reason shows on hover/focus; on touch
 * the field just stays red — a tap-popover would fight editing the field, and the same reason is listed
 * in the evaluation panel's error checklist, which is reachable there.
 *
 * The wrapper's element *type* stays identical whether or not an error is present — only the (portaled)
 * tooltip content toggles. Swapping the slot between a bare `<div>` and a tooltip wrapper as the error
 * appears/clears (which live validation does mid-edit) would remount the `<input>` inside it, dropping
 * keyboard focus while typing and stranding the next Tab. `coarse` is fixed per device, so that branch
 * never flips during editing.
 */
function LineField({
  error,
  className,
  children,
}: {
  error?: string;
  className?: string;
  children: ReactNode;
}) {
  const coarse = useCoarsePointer();
  if (coarse) return <div className={className}>{children}</div>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={className}>{children}</div>
      </TooltipTrigger>
      {error ? <TooltipContent>{error}</TooltipContent> : null}
    </Tooltip>
  );
}

/**
 * Unit-price field that remembers exactly what the user typed while editing, but shows a grouped,
 * locale-formatted amount once it loses focus — re-focusing restores the raw remembered value. The
 * [label] says whether the amount is net or gross, which is the scheme's most load-bearing difference.
 */
function PriceInput({
  label,
  value,
  onChange,
  disabled,
  invalid,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
}) {
  const { i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? i18n.language;
  const [focused, setFocused] = useState(false);
  return (
    <Input
      aria-label={label}
      placeholder={label}
      inputMode="decimal"
      autoComplete="off"
      disabled={disabled}
      aria-invalid={invalid || undefined}
      value={focused ? value : formatPriceDisplay(value, lng)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => onChange(sanitizeDecimal(e.target.value))}
    />
  );
}

/** A blurred price → grouped, at least two decimals, in the active locale; blanks and non-numbers pass through. */
function formatPriceDisplay(raw: string, lng: string): string {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  const n = Number(trimmed.replace(',', '.'));
  if (!Number.isFinite(n)) return raw;
  return new Intl.NumberFormat(lng, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 20,
  }).format(n);
}

/**
 * Editable unit field: a free-text input with a dropdown that always lists every suggested unit
 * (unlike a native datalist, it never filters by what's typed). Picking one fills the field.
 */
function UnitPicker({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <Input
        aria-label={t('invoices.compose.fields.unit')}
        placeholder={t('invoices.compose.fields.unit')}
        autoComplete="off"
        className="pr-8"
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            // Mouse-only affordance: the field is free-text, so keep the chevron out of the Tab order
            // — Tab moves straight from the unit input to the next field, and keyboard users just type.
            tabIndex={-1}
            disabled={disabled}
            aria-label={t('invoices.compose.fields.unit_select')}
            className="absolute inset-y-0 right-0 flex items-center pr-2.5 outline-none focus-visible:text-foreground disabled:opacity-50"
          >
            <ChevronsUpDown className="size-3.5 opacity-50" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {UNIT_OPTIONS.map((unit) => (
            <DropdownMenuItem key={unit} onSelect={() => onChange(unit)}>
              <Check className={cn('size-4', value === unit ? 'opacity-100' : 'opacity-0')} />
              {unit}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * The margin-procedure choice (art. 119 travel vs the three art. 120 goods categories). FA(3) requires
 * exactly one marker beside `P_PMarzy`, so there is no sensible default — the picker starts empty and
 * the evaluator reports `MARGIN_PROCEDURE_MISSING` until one is chosen.
 */
export function MarginProcedurePicker({
  value,
  onChange,
  disabled,
  invalid,
}: {
  value: MarginProcedure | '';
  onChange: (procedure: MarginProcedure) => void;
  disabled?: boolean;
  invalid?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-label={t('invoices.margin.procedure')}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', value === '' && 'text-muted-foreground')}>
            {value === ''
              ? t('invoices.margin.procedure_placeholder')
              : t(`invoices.margin.procedures.${value}`)}
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-w-sm">
        {MARGIN_PROCEDURES.map((procedure) => (
          <DropdownMenuItem key={procedure} onSelect={() => onChange(procedure)}>
            <Check className={cn('size-4', value === procedure ? 'opacity-100' : 'opacity-0')} />
            {t(`invoices.margin.procedures.${procedure}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function RatePicker({
  value,
  onChange,
  disabled,
  invalid,
}: {
  value: VatRate | '';
  onChange: (rate: VatRate) => void;
  disabled?: boolean;
  invalid?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', value === '' && 'text-muted-foreground')}>
            {value === ''
              ? t('invoices.compose.fields.vat_rate')
              : t(`invoices.compose.vat_rate.${value}`)}
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {V1_SELECTABLE_VAT_RATES.map((rate) => (
          <DropdownMenuItem key={rate} onSelect={() => onChange(rate)}>
            <Check className={cn('size-4', value === rate ? 'opacity-100' : 'opacity-0')} />
            {t(`invoices.compose.vat_rate.${rate}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
